import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import { Download, FileSpreadsheet, FileText } from "lucide-react";

const REPORT_LIST = [
  { key: "quality_audit", label: "Quality Audit Report", desc: "All audited charts with full detail (per-chart row with separate Coding & OASIS error counts)." },
  { key: "coding_quality", label: "Coding Quality Report", desc: "Charts with Coding audit data — uses Coding score & Coding errors." },
  { key: "oasis_quality", label: "OASIS Quality Report", desc: "Charts with OASIS audit data — uses OASIS score & OASIS errors." },
  { key: "employee_quality", label: "Employee-wise Quality Report", desc: "Per-coder roll-up of charts, scores, errors." },
  { key: "team_lead_quality", label: "Team Lead-wise Quality Report", desc: "Per-team-lead roll-up." },
  { key: "qa_performance", label: "QA-wise Audit Performance Report", desc: "Per-QA productivity and average score." },
  { key: "error_category", label: "Error Category Report", desc: "Errors grouped by category and section." },
  { key: "error_trend", label: "Error Trend Analysis Report", desc: "Daily error counts split by Coding / OASIS." },
  { key: "pending_ack", label: "Pending QA Acknowledgement Report", desc: "Audits awaiting coder acknowledgement (with overdue flagging)." },
  { key: "re_audit", label: "Re-Audit Report", desc: "Charts in rework / re-audit." },
  { key: "custom_range", label: "Daily / Weekly / Monthly / Custom Range", desc: "Same as Quality Audit Report, scoped to your selected date range." },
];

export default function Reports() {
  const [filters, setFilters] = useState({
    audit_type: "",
    status: "",
    coder_id: "",
    qa_id: "",
    date_from: "",
    date_to: "",
  });
  const [coders, setCoders] = useState([]);
  const [qas, setQas] = useState([]);
  const [downloading, setDownloading] = useState("");

  useEffect(() => {
    Promise.all([api.get("/users"), api.get("/users/qa-list").catch(() => ({ data: [] }))])
      .then(([u, q]) => {
        setCoders(u.data.filter((x) => x.role === "coder"));
        setQas(q.data);
      })
      .catch(() => { /* non-blocking */ });
  }, []);

  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  const download = async (report_type, fmt) => {
    setDownloading(`${report_type}_${fmt}`);
    try {
      const params = new URLSearchParams({ report_type, fmt });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await api.get(`/reports/quality?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: fmt === "xlsx" ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : "text/csv",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report_type}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${report_type} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setDownloading("");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="reports-page">
      <PageHeader
        title="Quality Reports"
        subtitle="Download audit and quality reports in CSV or Excel. Restricted to QA, Team Lead, Manager and Admin."
      />

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <h3 className="font-heading text-base font-medium mb-3">Filters (optional)</h3>
        <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Audit Type</label>
            <select value={filters.audit_type} onChange={(e) => setF("audit_type", e.target.value)} data-testid="filter-audit-type"
                    className="w-full border border-slate-300 rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-sky-500 outline-none">
              <option value="">All</option>
              <option value="coding">Coding</option>
              <option value="oasis">OASIS</option>
              <option value="combined">Combined (Coding + OASIS)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Audit Status</label>
            <select value={filters.status} onChange={(e) => setF("status", e.target.value)} data-testid="filter-status"
                    className="w-full border border-slate-300 rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-sky-500 outline-none">
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="in_audit">In Audit</option>
              <option value="completed">Completed</option>
              <option value="rework">Rework</option>
              <option value="re_audit">Re-Audit</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Employee</label>
            <select value={filters.coder_id} onChange={(e) => setF("coder_id", e.target.value)} data-testid="filter-coder"
                    className="w-full border border-slate-300 rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-sky-500 outline-none">
              <option value="">All</option>
              {coders.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">QA</label>
            <select value={filters.qa_id} onChange={(e) => setF("qa_id", e.target.value)} data-testid="filter-qa"
                    className="w-full border border-slate-300 rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-sky-500 outline-none">
              <option value="">All</option>
              {qas.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Date From</label>
            <input type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} data-testid="filter-date-from"
                   className="w-full border border-slate-300 rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-sky-500 outline-none"/>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-500 mb-1">Date To</label>
            <input type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} data-testid="filter-date-to"
                   className="w-full border border-slate-300 rounded-lg px-2.5 py-2 focus:ring-2 focus:ring-sky-500 outline-none"/>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {REPORT_LIST.map((r) => (
          <div key={r.key} className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`report-${r.key}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-700 grid place-items-center flex-shrink-0">
                <FileText size={16}/>
              </div>
              <div className="min-w-0">
                <div className="font-medium text-slate-900 text-sm">{r.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{r.desc}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => download(r.key, "csv")} disabled={!!downloading}
                      data-testid={`download-${r.key}-csv`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-slate-300 text-slate-800 rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-slate-50 disabled:opacity-60">
                <Download size={12}/> {downloading === `${r.key}_csv` ? "…" : "CSV"}
              </button>
              <button onClick={() => download(r.key, "xlsx")} disabled={!!downloading}
                      data-testid={`download-${r.key}-xlsx`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-700 text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-emerald-600 disabled:opacity-60">
                <FileSpreadsheet size={12}/> {downloading === `${r.key}_xlsx` ? "…" : "Excel"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
